import type { RecurringCadence } from '../constants/recurring';

const MS_PER_DAY = 86_400_000;

interface CalendarDate {
  year: number;
  month: number; // 0-based, as Date.UTC takes it
  day: number;
}

function parse(date: string): CalendarDate {
  const [year, month, day] = date.split('-').map(Number);
  return { year, month: month - 1, day };
}

function format(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

function toUtcMs(date: string): number {
  const { year, month, day } = parse(date);
  return Date.UTC(year, month, day);
}

function lastDayOfMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
}

/** The anchor shifted by `months`, clamped to the target month's last day. */
function addMonthsClamped(anchor: CalendarDate, months: number): string {
  const total = anchor.month + months;
  const year = anchor.year + Math.floor(total / 12);
  const month = ((total % 12) + 12) % 12;
  const day = Math.min(anchor.day, lastDayOfMonth(year, month));
  return format(Date.UTC(year, month, day));
}

export function daysBetween(a: string, b: string): number {
  return Math.round((toUtcMs(b) - toUtcMs(a)) / MS_PER_DAY);
}

export function laterDate(a: string, b: string): string {
  return a >= b ? a : b;
}

export function previousDay(date: string): string {
  return format(toUtcMs(date) - MS_PER_DAY);
}

export function todayUtc(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10);
}

/** The first date strictly after `after` on the cadence anchored at `anchorDate`. */
export function nextOccurrence(
  anchorDate: string,
  cadence: RecurringCadence,
  after: string
): string {
  if (anchorDate > after) return anchorDate;

  if (cadence.unit === 'day' || cadence.unit === 'week') {
    const stepDays =
      cadence.unit === 'week' ? 7 * cadence.every : cadence.every;
    const steps = Math.floor(daysBetween(anchorDate, after) / stepDays) + 1;
    return format(toUtcMs(anchorDate) + steps * stepDays * MS_PER_DAY);
  }

  const step = cadence.unit === 'month' ? cadence.every : 12 * cadence.every;
  const anchor = parse(anchorDate);
  const target = parse(after);
  const monthsApart =
    (target.year - anchor.year) * 12 + (target.month - anchor.month);
  // Each step is recomputed from the anchor so a 31st (or 29 Feb) clamp never sticks.
  let n = Math.floor(monthsApart / step);
  let candidate = addMonthsClamped(anchor, n * step);
  while (candidate <= after) {
    n += 1;
    candidate = addMonthsClamped(anchor, n * step);
  }
  return candidate;
}
